import {
  type ReplaceSupplierSalesRequest,
  type SupplierRevenueRange,
  type SupplierSalesYear
} from '@tandemone/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { useState } from 'react'

import { extractErrorMessage } from '@/api/extract-error'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

import { getSupplierSales, replaceSupplierSales } from './api'
import {
  SUPPLIER_REVENUE_RANGE_LABELS,
  SUPPLIER_REVENUE_RANGES
} from './enum-labels'
import { FormSkeleton } from './form-skeleton'
import { invalidateOnboarding } from './invalidate-onboarding'
import { PrimaryButton } from './primary-button'
import { StepFooter } from './step-footer'

function targetYears(): number[] {
  const current = new Date().getUTCFullYear()
  return [current - 1, current - 2, current - 3]
}

type Picks = Record<number, SupplierRevenueRange | null>

function buildInitial(years: number[], existing: SupplierSalesYear[]): Picks {
  const byYear = new Map(existing.map(row => [row.year, row.revenueRange]))
  const picks: Picks = {}
  for (const year of years) picks[year] = byYear.get(year) ?? null
  return picks
}

export function SupplierSalesForm({
  onSuccess,
  submitLabel = 'Save and continue'
}: {
  onSuccess: () => void
  submitLabel?: string
}) {
  const queryClient = useQueryClient()
  const years = targetYears()
  const [picks, setPicks] = useState<Picks | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['supplier-sales'],
    queryFn: ({ signal }) => getSupplierSales(signal)
  })

  const mutation = useMutation({
    mutationFn: (body: ReplaceSupplierSalesRequest) =>
      replaceSupplierSales(body),
    onSuccess: async () => {
      await invalidateOnboarding(queryClient, 'supplier-sales')
      onSuccess()
    },
    onError: (err: unknown) => setSubmitError(extractErrorMessage(err))
  })

  if (isLoading) return <FormSkeleton rows={3} />

  const state = picks ?? buildInitial(years, data?.years ?? [])
  const pickedCount = years.filter(year => state[year] !== null).length
  const canSubmit = pickedCount >= 1

  function patch(year: number, value: SupplierRevenueRange) {
    setPicks(prev => ({ ...(prev ?? state), [year]: value }))
    setSubmitError(null)
  }

  function clearYear(year: number) {
    setPicks(prev => ({ ...(prev ?? state), [year]: null }))
    setSubmitError(null)
  }

  function handleSubmit() {
    if (!canSubmit || mutation.isPending) return
    setSubmitError(null)
    mutation.mutate({
      years: years
        .filter(year => state[year] !== null)
        .map(year => ({
          year,
          revenueRange: state[year] as SupplierRevenueRange
        }))
    })
  }

  return (
    <div className='flex w-full flex-col gap-4'>
      <section className='rounded-2xl border border-border bg-page p-6'>
        <div className='flex flex-col gap-4'>
          {years.map(year => {
            const id = `sales-year-${year}`
            return (
              <div
                key={year}
                className='flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4'
              >
                <Label
                  htmlFor={id}
                  className='w-32 shrink-0 text-[13px] font-medium text-ink'
                >
                  Fiscal year {year}
                </Label>
                <div className='flex flex-1 items-center gap-2'>
                  <Select
                    value={state[year] ?? undefined}
                    onValueChange={value =>
                      patch(year, value as SupplierRevenueRange)
                    }
                  >
                    <SelectTrigger id={id} className='w-full sm:w-full'>
                      <SelectValue placeholder='Select revenue range' />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPLIER_REVENUE_RANGES.map(range => (
                        <SelectItem key={range} value={range}>
                          {SUPPLIER_REVENUE_RANGE_LABELS[range]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {state[year] !== null && (
                    <button
                      type='button'
                      onClick={() => clearYear(year)}
                      aria-label={`Clear fiscal year ${year}`}
                      className='grid size-8 shrink-0 cursor-pointer place-items-center rounded-md text-ink-muted transition-colors hover:bg-cream/40 hover:text-ink'
                    >
                      <X className='size-3.5' />
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </section>

      <StepFooter
        hint={
          pickedCount === 0
            ? 'Pick at least one fiscal year'
            : `${pickedCount} of 3 selected`
        }
      >
        <PrimaryButton
          type='button'
          disabled={!canSubmit || mutation.isPending}
          onClick={handleSubmit}
        >
          {mutation.isPending ? 'Saving…' : submitLabel}
        </PrimaryButton>
      </StepFooter>

      {submitError && <p className='text-sm text-red-600'>{submitError}</p>}
    </div>
  )
}
